/**
 * Erfassungsseite (Etappe 1, F2/F4–F8): Kern-Seite + austauschbare
 * Erfassungsquelle (Adapter-Schnitt, SCHNITTLISTE §5).
 *
 * Der Kern (diese Datei) hält Erfassungs-Chat, Screenshot-Liste, Annotation,
 * Formular und Absenden — in beiden Linien identisch. Die Quelle links ist
 * je Anwendung austauschbar:
 *  - „web-tab"    (WebTabSource):    Web-Anwendung im eigenen Browser-Tab
 *    (Standard, Issue #61) — Screenshot per Tab-Freigabe mit Live-Vorschau,
 *    kein gemeinsamer Origin mit der Anwendung.
 *  - „web-iframe" (WebIframeSource): Alt-Modus same-origin-iframe,
 *    Screenshot mit iframe-Zuschnitt, Route-Auslesung für den Kontext
 *    (nur noch per VITE_WEB_EINBETTUNG=iframe).
 *  - „ninox"      (NinoxTabSource):  Ninox im Fremd-Tab (Deep-Link, Live/DEV),
 *    Screenshot per Tab-Freigabe; Kontext über Tabellen-Auswahl +
 *    eingefügte Ninox-URL (cross-origin, F8).
 *
 * Auswahl: `erfassungsquelle()` — ninox, wenn die Ninox-Zuordnung der
 * Anwendung gesetzt ist (bzw. der Deep-Link eindeutig eine Ninox-URL ist),
 * sonst web-tab bzw. web-iframe je Einbettungs-Modus.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  Maximize2,
  Minimize2,
  Pencil,
} from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { erfassungsquelle, istNinoxAnwendung } from "@/components/AppSwitcher"
import { AnnotationEditor } from "@/components/erfassung/AnnotationEditor"
import { ErfassungsChat } from "@/components/erfassung/ErfassungsChat"
import { ScreenshotBild } from "@/components/erfassung/ScreenshotBild"
import { NinoxTabSource } from "@/components/erfassung/quellen/NinoxTabSource"
import { WebIframeSource } from "@/components/erfassung/quellen/WebIframeSource"
import { WebTabSource } from "@/components/erfassung/quellen/WebTabSource"
import { dateiAlsBlob } from "@/lib/screenshot"
import { api, apiError } from "@/services/api"
import type {
  Annotation,
  Application,
  ChatNachricht,
  Prioritaet,
  Report,
  ReportKontext,
  ReportTyp,
  Screenshot,
} from "@/types"

export function ErfassungPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const entwurfParam = searchParams.get("entwurf")
  // Zielbild #36: Der Aufrufer (Käfer-Verweis der Anwendung) weiß, aus
  // welcher Stufe er kommt, und übergibt sie — der Melder muss die Umgebung
  // nicht mehr selbst wissen. `prod`/`live` ⇒ live, alles andere ⇒ dev.
  const umgebungParam = searchParams.get("umgebung")

  const dateiInputRef = useRef<HTMLInputElement>(null)
  // Route-Auslesung der web-Quelle (WebIframeSource registriert den Leser)
  const routeLeserRef = useRef<(() => string | null) | null>(null)

  const [appId, setAppId] = useState<number | null>(null)
  const [entwurfId, setEntwurfId] = useState<number | null>(
    entwurfParam ? Number(entwurfParam) : null
  )
  // ninox-Quelle: Rohbild → Annotations-Editor → Upload (Fork-Verhalten)
  const [rohBild, setRohBild] = useState<Blob | null>(null)
  const [screenshots, setScreenshots] = useState<Screenshot[]>([])
  const [chatNachrichten, setChatNachrichten] = useState<ChatNachricht[]>([])
  const [kiVorschau, setKiVorschau] = useState<Report | null>(null)

  // Umgebung der Meldung: live = echte Daten, dev = Stage/Testdaten.
  // ninox: Screenshot-Umgebung (Δ Ninox). web: welche Stufe im Rahmen läuft —
  // vorbelegt vom Aufrufer (?umgebung=…), umschaltbar im Rahmenkopf.
  const [umgebung, setUmgebung] = useState<"live" | "dev">(
    umgebungParam === "prod" || umgebungParam === "live" ? "live" : "dev"
  )

  // Formular-Zustand — Typ bewusst OHNE Vorauswahl (setzt die KI-Strukturierung)
  const [typ, setTyp] = useState<ReportTyp | null>(null)
  const [titel, setTitel] = useState("")
  const [istBeschreibung, setIstBeschreibung] = useState("")
  const [sollBeschreibung, setSollBeschreibung] = useState("")
  const [prioritaet, setPrioritaet] = useState<Prioritaet>("mittel")

  // Seitenleiste „Neue Meldung": Breite per Ziehgriff änderbar (gemerkt im
  // Browser), Abschnitte als Akkordeon — genau EINER offen, damit der Melder
  // die Leiste von oben nach unten durchgehen kann.
  type SektionId = "dialog" | "einordnung" | "beschreibung" | "screenshots"
  const [leisteBreite, setLeisteBreite] = useState(() => {
    const wert = Number(localStorage.getItem("tester.meldeleiste.breite"))
    return Number.isFinite(wert) && wert >= 380 && wert <= 900 ? wert : 520
  })
  const [leisteZieht, setLeisteZieht] = useState(false)
  const [offeneSektion, setOffeneSektion] = useState<SektionId | null>("dialog")
  const leisteZiehen = (e: React.PointerEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startBreite = leisteBreite
    setLeisteZieht(true)
    const move = (ev: PointerEvent) => {
      // Griff sitzt am LINKEN Rand: nach links ziehen = breiter
      setLeisteBreite(Math.min(900, Math.max(380, startBreite + (startX - ev.clientX))))
    }
    const up = () => {
      setLeisteZieht(false)
      setLeisteBreite((b) => {
        localStorage.setItem("tester.meldeleiste.breite", String(b))
        return b
      })
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }
  // Nach der KI-Zusammenfassung weiter zum nächsten Schritt: die KI hat Typ
  // und Felder gefüllt — der Melder prüft als Nächstes die Einordnung.
  useEffect(() => {
    if (kiVorschau) setOffeneSektion("einordnung")
  }, [kiVorschau])

  // „Neue Meldung" als bewegliches Fenster (mehr Überblick) — geführte
  // Strecke in drei Tabs: Screenshot (volle Größe) → Weiter →
  // Erfassungs-Assistent → KI-Übergabe → Ergebnis (Mini-PRD)
  const [alsFenster, setAlsFenster] = useState(false)
  const [fensterTab, setFensterTab] = useState<"screenshot" | "dialog" | "ergebnis">("screenshot")
  const [screenshotIndex, setScreenshotIndex] = useState(0)
  const [fensterPos, setFensterPos] = useState({ x: 80, y: 70 })
  const ziehenRef = useRef<{ dx: number; dy: number } | null>(null)
  const fensterZiehen = (e: React.PointerEvent) => {
    ziehenRef.current = { dx: e.clientX - fensterPos.x, dy: e.clientY - fensterPos.y }
    const move = (ev: PointerEvent) => {
      const start = ziehenRef.current
      if (!start) return
      setFensterPos({
        x: Math.max(0, Math.min(window.innerWidth - 200, ev.clientX - start.dx)),
        y: Math.max(0, Math.min(window.innerHeight - 80, ev.clientY - start.dy)),
      })
    }
    const up = () => {
      ziehenRef.current = null
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
  })
  const anwendung: Application | undefined = useMemo(() => {
    if (appId !== null) return anwendungen?.find((a) => a.id === appId)
    if (!anwendungen || anwendungen.length === 0) return undefined
    if (anwendungen.length === 1) return anwendungen[0]
    // Mehrere Anwendungen pro Instanz: die zur aufgerufenen Domain passende
    // wählen — der Tester läuft same-origin im Vhost jeder web-Anwendung
    // (§2.1). Ninox-Anwendungen laufen extern und matchen nie → für sie
    // bleibt es bei der expliziten Auswahl (Δ Ninox). KEIN stiller
    // Fallback auf die erste App: ein falsch zugeordneter Report erzeugt
    // sein Issue im falschen Repo — lieber explizit wählen.
    return anwendungen.find((a) => {
      try {
        // Beide Stufen zählen: läuft der Tester unter eigener Adresse
        // (Zielbild #36), matcht keine — dann bleibt die explizite Auswahl.
        if (new URL(a.url).origin === window.location.origin) return true
        return !!a.urlLive && new URL(a.urlLive).origin === window.location.origin
      } catch {
        return false
      }
    })
  }, [anwendungen, appId])

  // Quellen-Auswahl je Anwendung (Adapter-Schnitt): ninox vs. web
  const quelle = anwendung !== undefined ? erfassungsquelle(anwendung) : null
  const ninoxQuelle = quelle === "ninox"

  // Bereitschafts-Gate: Ninox-Anwendung erst meldbar, wenn nxtools das Team
  // analysiert hat (Struktur für den KI-Dialog). null/true = kein Gate.
  const ninoxStatus = useQuery({
    queryKey: ["applications", anwendung?.id, "ninox-status"],
    queryFn: () => api.applications.ninoxStatus(anwendung!.id),
    enabled: ninoxQuelle && anwendung !== undefined,
    refetchInterval: (q) => (q.state.data && q.state.data.bereit === false ? 30_000 : false),
  })
  const meldenGesperrtGrund =
    ninoxQuelle && ninoxStatus.data?.bereit === false
      ? "Die Anwendung wird vorbereitet — die Struktur-Analyse läuft. In wenigen Minuten geht es los."
      : undefined

  // Entwurf fortsetzen (?entwurf=<id>)
  useQuery({
    queryKey: ["reports", entwurfId],
    queryFn: async () => {
      const detail = await api.reports.get(entwurfId!)
      setAppId(detail.applicationId)
      setTyp(detail.typ)
      setTitel(detail.titel)
      setIstBeschreibung(detail.istBeschreibung ?? "")
      setSollBeschreibung(detail.sollBeschreibung ?? "")
      setPrioritaet(detail.prioritaet)
      setScreenshots(detail.screenshots)
      setChatNachrichten(detail.chat)
      return detail
    },
    enabled: entwurfId !== null && titel === "" && screenshots.length === 0,
    staleTime: Infinity,
  })

  /** F8 (ninox): Kontext aus der App-Zuordnung. Die betroffene Tabelle und
      Stelle ermittelt die KI aus Dialog + Screenshots gegen die DEV-Struktur
      aus nxtools — der Melder wählt nichts von Hand. */
  const kontextNinox = (app: Application): ReportKontext => {
    return {
      // Screenshot-Umgebung (Δ Ninox): live = echte Daten, dev = Testdaten
      umgebung,
      browser: navigator.userAgent,
      zeitpunkt: new Date().toISOString(),
      // Referenz für die Einordnung: welche App war gewählt
      applicationUrl: app.url,
    }
  }

  /** Aktive URL der web-Quelle: live nur, wenn `urlLive` gepflegt ist —
      sonst Fallback auf die Standard-/Stage-URL (Spiegel von openApp/ninox). */
  const webUrl = (app: Application): string =>
    umgebung === "live" && app.urlLive ? app.urlLive : app.url

  /** F8 (web): Kontext-Metadaten automatisch erfassen — Route aus dem
      same-origin-iframe (über die Quelle), Version aus dem Health-Endpoint. */
  const kontextWeb = async (app: Application): Promise<ReportKontext> => {
    const aktiveUrl = webUrl(app)
    const route = routeLeserRef.current?.() ?? null
    let appVersion: string | null = null
    try {
      const res = await fetch(`${aktiveUrl}/api/v1/health`, { signal: AbortSignal.timeout(2000) })
      appVersion = (await res.json()).version ?? null
    } catch {
      appVersion = null
    }
    return {
      route,
      appVersion,
      // Zielbild #36: gegen welche Stufe wurde geprüft — steht in der
      // Meldung und im Issue, die Entwicklung muss nicht mehr raten.
      umgebung,
      applicationUrl: aktiveUrl,
      browser: navigator.userAgent,
      zeitpunkt: new Date().toISOString(),
    }
  }

  /** Kontext je Quelle (Adapter-Schnitt) */
  const kontextErmitteln = (app: Application): Promise<ReportKontext> =>
    istNinoxAnwendung(app) ? Promise.resolve(kontextNinox(app)) : kontextWeb(app)

  const beginnen = useMutation({
    mutationFn: async () => {
      if (!anwendung) throw new Error("Keine Anwendung")
      const kontext = await kontextErmitteln(anwendung)
      return api.reports.create({ applicationId: anwendung.id, kontext })
    },
    onSuccess: (report) => setEntwurfId(report.id),
    onError: (e) => toast.error(apiError(e)),
  })

  // Screenshot aus der Quelle: web lädt direkt hoch (Annotationen bei
  // Bedarf nachträglich), ninox öffnet zuerst den Annotations-Editor.
  const bildAufgenommen = (bild: Blob) => {
    if (ninoxQuelle) setRohBild(bild)
    else screenshotHochladen.mutate(bild)
  }

  const screenshotHochladen = useMutation({
    mutationFn: (bild: Blob) => api.reports.screenshots.upload(entwurfId!, bild, []),
    onSuccess: (screenshot) => {
      setScreenshots((s) => [...s, screenshot])
      toast.success("Screenshot hinzugefügt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // ninox: Abschluss des Annotations-Editors → Upload mit Annotationen
  const annotationFertig = useMutation({
    mutationFn: ({ bild, annotationen }: { bild: Blob; annotationen: Annotation[] }) =>
      api.reports.screenshots.upload(entwurfId!, bild, annotationen),
    onSuccess: (screenshot) => {
      setScreenshots((s) => [...s, screenshot])
      setRohBild(null)
      toast.success("Screenshot hinzugefügt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const screenshotEntfernen = useMutation({
    mutationFn: (screenshotId: number) => api.reports.screenshots.remove(entwurfId!, screenshotId),
    onSuccess: (_d, screenshotId) =>
      setScreenshots((s) => s.filter((x) => x.id !== screenshotId)),
    onError: (e) => toast.error(apiError(e)),
  })

  // Nachbearbeitung eines gespeicherten Screenshots (Fenster-Tab 1):
  // Bild laden → Annotation-Editor → Übernehmen ersetzt das Bild
  const [bearbeitung, setBearbeitung] = useState<{ blob: Blob; altId: number } | null>(null)

  const bearbeitenOeffnen = async (screenshotId: number) => {
    try {
      const blob = await api.reports.screenshots.datei(entwurfId!, screenshotId)
      setBearbeitung({ blob, altId: screenshotId })
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const bearbeitungFertig = useMutation({
    mutationFn: async ({
      bild,
      annotationen,
      altId,
    }: {
      bild: Blob
      annotationen: Annotation[]
      altId: number
    }) => {
      const neu = await api.reports.screenshots.upload(entwurfId!, bild, annotationen)
      await api.reports.screenshots.remove(entwurfId!, altId)
      return neu
    },
    onSuccess: (neu, { altId }) => {
      setScreenshots((s) => [...s.filter((x) => x.id !== altId), neu])
      setScreenshotIndex(Math.max(0, screenshots.length - 1))
      setBearbeitung(null)
      toast.success("Screenshot aktualisiert")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // §8: Abschluss → finalize → Mini-PRD-Vorschau (Felder werden vorbefüllt)
  const kiZusammenfassen = useMutation({
    mutationFn: async () => {
      // Manuelle Eingaben zuerst sichern — sie fließen in die Strukturierung
      // ein. ninox: aktuellen Kontext (Tabellen-Auswahl/URL) mitschreiben;
      // web: den beim Start erfassten Kontext (Route) nicht überschreiben.
      await api.reports.update(entwurfId!, {
        typ: typ ?? undefined,
        titel: titel || undefined,
        istBeschreibung: istBeschreibung || null,
        sollBeschreibung: sollBeschreibung || null,
        ...(ninoxQuelle && anwendung ? { kontext: kontextNinox(anwendung) } : {}),
      })
      return api.reports.finalize(entwurfId!)
    },
    onSuccess: (report) => {
      setTitel(report.titel)
      setTyp(report.typ)
      setIstBeschreibung(report.istBeschreibung ?? "")
      setSollBeschreibung(report.sollBeschreibung ?? "")
      setPrioritaet(report.prioritaet)
      setKiVorschau(report)
      // Im Fenster-Modus direkt zum Ergebnis-Tab springen
      setFensterTab("ergebnis")
      toast.success("Mini-PRD erstellt — bitte prüfen und absenden")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const absenden = useMutation({
    mutationFn: async () => {
      await api.reports.update(entwurfId!, {
        typ: typ ?? undefined,
        titel,
        istBeschreibung: istBeschreibung || null,
        sollBeschreibung: sollBeschreibung || null,
        prioritaet,
        ...(ninoxQuelle && anwendung ? { kontext: kontextNinox(anwendung) } : {}),
      })
      return api.reports.absenden(entwurfId!)
    },
    onSuccess: (report) => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
      toast.success("Meldung abgesendet")
      navigate(`/meldungen/${report.id}`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const entwurfVerwerfen = useMutation({
    mutationFn: () => api.reports.remove(entwurfId!),
    onSuccess: () => {
      setEntwurfId(null)
      setScreenshots([])
      setTitel("")
      setIstBeschreibung("")
      setSollBeschreibung("")
      toast.success("Entwurf verworfen")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  if (!anwendungen) return <p className="text-muted-foreground">Lade Anwendungen …</p>
  if (anwendungen.length === 0) {
    return (
      <p className="rounded-md bg-warning/10 px-4 py-3 text-warning">
        Keine aktive Anwendung konfiguriert — Anlage durch netz-fabrik (F3).
      </p>
    )
  }
  if (!anwendung) {
    // Laufzeit-Guard: Domain keiner Anwendung zugeordnet → explizite Auswahl
    return (
      <div className="mx-auto max-w-lg pt-10">
        <h1 className="mb-1.5 text-[22px] font-bold">Welche Anwendung möchtest du testen?</h1>
        <p className="mb-5 text-sm text-muted-foreground">
          Diese Adresse ist keiner Anwendung eindeutig zugeordnet — bitte wähle aus, damit
          die Meldung im richtigen Projekt landet.
        </p>
        <div className="flex flex-col gap-2.5">
          {anwendungen.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setAppId(a.id)}
              className="flex items-center justify-between rounded-xl border bg-card px-5 py-4 text-left shadow-card transition-colors hover:border-primary/40"
            >
              <span>
                <span className="block text-[15px] font-semibold">{a.name}</span>
                <span className="mt-0.5 block text-[13px] text-muted-foreground">{a.url}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
            </button>
          ))}
        </div>
      </div>
    )
  }

  const absendenMoeglich =
    typ !== null &&
    titel.trim() !== "" &&
    (istBeschreibung.trim() !== "" || sollBeschreibung.trim() !== "")

  return (
    <div className="flex gap-4" style={{ height: "calc(100vh - 8rem)" }}>
      {/* Erfassungsquelle (F2) — je Anwendung: Fremd-Tab (web-tab, ninox)
          oder Alt-Modus iframe. key erzwingt Remount (frische Capture-
          Session) je App. */}
      {ninoxQuelle ? (
        <NinoxTabSource
          key={anwendung.id}
          anwendung={anwendung}
          meldenGesperrtGrund={meldenGesperrtGrund}
          entwurfId={entwurfId}
          meldenStarten={() => beginnen.mutate()}
          meldenLaeuft={beginnen.isPending}
          onBild={bildAufgenommen}
          onDateiWaehlen={() => dateiInputRef.current?.click()}
          onUmgebungChange={setUmgebung}
        />
      ) : quelle === "web-tab" ? (
        <WebTabSource
          key={anwendung.id}
          anwendung={anwendung}
          entwurfId={entwurfId}
          meldenStarten={() => beginnen.mutate()}
          meldenLaeuft={beginnen.isPending}
          onBild={bildAufgenommen}
          onDateiWaehlen={() => dateiInputRef.current?.click()}
          onUmgebungChange={setUmgebung}
        />
      ) : (
        <WebIframeSource
          key={anwendung.id}
          anwendung={anwendung}
          url={webUrl(anwendung)}
          umgebung={umgebung}
          onUmgebungChange={setUmgebung}
          entwurfId={entwurfId}
          meldenStarten={() => beginnen.mutate()}
          meldenLaeuft={beginnen.isPending}
          onBild={bildAufgenommen}
          onDateiWaehlen={() => dateiInputRef.current?.click()}
          routeLeserRef={routeLeserRef}
        />
      )}

      {/* Fallback-Upload (beide Quellen): verstecktes Datei-Feld des Kerns —
          auch die „Bild hochladen"-Buttons im Fenster-Modus zielen hierauf */}
      <input
        ref={dateiInputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) {
            if (ninoxQuelle) {
              setRohBild(file) // Annotations-Editor zuerst (Fork-Verhalten)
            } else {
              // Fallback-Upload ebenfalls auf MAX_KANTE begrenzen (Upload-Limit)
              void dateiAlsBlob(file).then((b) => screenshotHochladen.mutate(b))
            }
          }
          e.target.value = ""
        }}
      />

      {/* Meldungsformular (F6/F22) — Inhalt für Seitenleiste UND Fenster */}
      {entwurfId !== null && (() => {
        // Uppercase-Feldbeschriftung (Design Screen 03)
        const LABEL = "mb-1.5 block text-xs font-bold uppercase tracking-wide text-foreground/70"

        // Bausteine — in Seitenleiste (eine Spalte) und Fenster (zwei Spalten)
        // unterschiedlich angeordnet
        const chatBlock = (
          <ErfassungsChat
            reportId={entwurfId}
            nachrichten={chatNachrichten}
            onNeueNachrichten={setChatNachrichten}
            hoch={alsFenster}
          />
        )

        // KI-Zusammenfassung direkt unter dem Dialog — sie füllt Typ + Felder
        const kiButtonBlock = (
          <button
            type="button"
            onClick={() => kiZusammenfassen.mutate()}
            disabled={
              kiZusammenfassen.isPending ||
              (chatNachrichten.length === 0 && !titel && !istBeschreibung && !sollBeschreibung)
            }
            title="Strukturiert Dialog + Screenshots zum Mini-PRD (F14)"
            className="self-start rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80 disabled:opacity-50"
          >
            {kiZusammenfassen.isPending ? "KI strukturiert …" : "KI-Zusammenfassung erstellen"}
          </button>
        )

        const vorschauBlock = kiVorschau && (
            <div className="rounded-[10px] border border-primary/15 bg-primary/5 px-3.5 py-3 text-xs">
              <p className="font-bold text-primary">KI-Zusammenfassung übernommen</p>
              {kiVorschau.akzeptanzkriterien && kiVorschau.akzeptanzkriterien.length > 0 && (
                <ul className="mt-1 list-disc pl-4">
                  {kiVorschau.akzeptanzkriterien.map((k, i) => (
                    <li key={i}>{k}</li>
                  ))}
                </ul>
              )}
              {kiVorschau.machbarkeit && (
                <p className="mt-1 text-muted-foreground">
                  Unverbindliche Einschätzung: Aufwand {kiVorschau.machbarkeit}
                  {kiVorschau.modulId ? ` · Bereich „${kiVorschau.modulId}“` : ""}
                </p>
              )}
              {kiVorschau.duplikatVon && (
                <p className="mt-1 font-medium text-warning">
                  Hinweis: Ähnliche Meldung #{kiVorschau.duplikatVon} existiert bereits — du
                  kannst diesen Entwurf löschen und dich der bestehenden Meldung anschließen.
                </p>
              )}
            </div>
          )

        const typBlock = (
          <div className="flex gap-2">
            {(["fehler", "aenderungswunsch"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTyp(t)}
                className={`flex-1 rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors ${
                  typ === t ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/80"
                }`}
              >
                {t === "fehler" ? "Fehler" : "Änderungswunsch"}
              </button>
            ))}
          </div>
        )

        const felderBlock = (
          <>
          <label className="text-sm">
            <span className={LABEL}>Titel *</span>
            <input
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              maxLength={255}
              className="w-full rounded-lg border px-3 py-2 text-[13px]"
              placeholder="Kurz und prägnant"
            />
          </label>

          <label className="text-sm">
            <span className={LABEL}>
              {typ === "aenderungswunsch"
                ? "Heutiger Zustand / Workaround"
                : "Was passiert (Ist)? *"}
            </span>
            <textarea
              value={istBeschreibung}
              onChange={(e) => setIstBeschreibung(e.target.value)}
              rows={3}
              className="w-full rounded-lg border px-3 py-2 text-[13px]"
            />
          </label>

          <label className="text-sm">
            <span className={LABEL}>
              {typ === "aenderungswunsch"
                ? "Gewünschtes Verhalten *"
                : "Was sollte passieren (Soll)?"}
            </span>
            <textarea
              value={sollBeschreibung}
              onChange={(e) => setSollBeschreibung(e.target.value)}
              rows={3}
              className="w-full rounded-lg border px-3 py-2 text-[13px]"
            />
          </label>

          <label className="text-sm">
            <span className={LABEL}>Priorität (aus Kundensicht)</span>
            <select
              value={prioritaet}
              onChange={(e) => setPrioritaet(e.target.value as Prioritaet)}
              className="w-full rounded-lg border px-3 py-2 text-[13px]"
            >
              <option value="niedrig">Niedrig</option>
              <option value="mittel">Mittel</option>
              <option value="hoch">Hoch</option>
            </select>
          </label>
          </>
        )

        const screenshotsBlock = (
          <div className="text-sm">
            <span className={LABEL}>
              Screenshots ({screenshots.length})
            </span>
            <div className="flex flex-col gap-2">
              {screenshots.map((s) => (
                <div key={s.id} className="relative">
                  <ScreenshotBild reportId={entwurfId} screenshot={s} />
                  <button
                    type="button"
                    onClick={() => screenshotEntfernen.mutate(s.id)}
                    className="absolute right-1 top-1 rounded bg-destructive px-1.5 py-0.5 text-[10px] font-bold text-destructive-foreground"
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              {screenshots.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Noch kein Screenshot — über die Leiste unten aufnehmen.
                </p>
              )}
            </div>
          </div>
        )

        const footerBlock = (
          <div className="mt-auto flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => entwurfVerwerfen.mutate()}
              className="rounded-lg bg-secondary px-3.5 py-2.5 text-[13px] font-semibold hover:bg-secondary/80"
            >
              Entwurf löschen
            </button>
            <button
              type="button"
              onClick={() => absenden.mutate()}
              disabled={!absendenMoeglich || absenden.isPending}
              className="flex-1 rounded-lg bg-primary px-3.5 py-2.5 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              Meldung absenden
            </button>
          </div>
        )

        return alsFenster ? (
          // Bewegliches Fenster (Drag am Kopf): breit + hoch, zwei Tabs —
          // 1) Erfassungs-Assistent (Chat füllt die Höhe), 2) Ergebnis
          // (Mini-PRD-Formular + Vorschau + Screenshots). Nach der
          // KI-Zusammenfassung wechselt das Fenster automatisch auf Tab 2.
          <div
            className="fixed z-40 flex h-[88vh] w-[1400px] max-w-[96vw] flex-col overflow-hidden rounded-[14px] border bg-card shadow-[0_24px_60px_rgba(15,23,42,0.28)]"
            style={{ left: fensterPos.x, top: fensterPos.y }}
          >
            <div
              onPointerDown={fensterZiehen}
              className="flex shrink-0 cursor-move select-none items-center justify-between border-b bg-background py-3 pl-5 pr-4"
            >
              <div className="flex items-center gap-2.5">
                <GripVertical className="h-3.5 w-3.5 text-muted-foreground/60" />
                <h2 className="text-[15px] font-bold">Neue Meldung</h2>
              </div>
              <button
                type="button"
                onClick={() => setAlsFenster(false)}
                onPointerDown={(e) => e.stopPropagation()}
                title="An den Rand andocken"
                className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
              >
                <Minimize2 className="h-3.5 w-3.5" /> Andocken
              </button>
            </div>

            {/* Tab-Leiste (geführte Strecke: 1 → 2 → 3) */}
            <div className="flex shrink-0 gap-1 border-b bg-background px-5">
              {(
                [
                  ["screenshot", `Screenshot${screenshots.length > 0 ? ` (${screenshots.length})` : ""}`],
                  ["dialog", "Erfassungs-Assistent"],
                  ["ergebnis", "Ergebnis (Mini-PRD)"],
                ] as const
              ).map(([wert, label]) => (
                <button
                  key={wert}
                  type="button"
                  onClick={() => setFensterTab(wert)}
                  className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                    fensterTab === wert
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                  {wert === "ergebnis" && kiVorschau && (
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </button>
              ))}
            </div>

            {fensterTab === "screenshot" ? (
              <div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
                {screenshots.length === 0 ? (
                  <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-[10px] border border-dashed bg-muted/30 px-6 text-center">
                    <p className="max-w-md text-sm text-muted-foreground">
                      Noch kein Screenshot. Nimm ihn über die Leiste unter der
                      Anwendung auf (dazu das Fenster kurz „Andocken“) — oder
                      lade ein Bild hoch.
                    </p>
                    <button
                      type="button"
                      onClick={() => dateiInputRef.current?.click()}
                      className="rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80"
                    >
                      Bild hochladen
                    </button>
                  </div>
                ) : (
                  <div className="relative min-h-0 flex-1 overflow-hidden rounded-[10px] border bg-muted/40">
                    <ScreenshotBild
                      gross
                      reportId={entwurfId}
                      screenshot={screenshots[Math.min(screenshotIndex, screenshots.length - 1)]}
                    />
                    <div className="absolute right-2 top-2 flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          const s = screenshots[Math.min(screenshotIndex, screenshots.length - 1)]
                          void bearbeitenOeffnen(s.id)
                        }}
                        className="flex items-center gap-1 rounded bg-primary px-2 py-1 text-[11px] font-bold text-primary-foreground hover:bg-primary-hover"
                      >
                        <Pencil className="h-3 w-3" /> Bearbeiten
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const s = screenshots[Math.min(screenshotIndex, screenshots.length - 1)]
                          screenshotEntfernen.mutate(s.id)
                          setScreenshotIndex(0)
                        }}
                        className="rounded bg-destructive px-2 py-1 text-[11px] font-bold text-destructive-foreground"
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex shrink-0 items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {screenshots.length > 0 && (
                      <button
                        type="button"
                        onClick={() => dateiInputRef.current?.click()}
                        className="rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80"
                      >
                        Bild hochladen
                      </button>
                    )}
                    {screenshots.length > 1 && (
                      <>
                        <button
                          type="button"
                          title="Vorheriges Bild"
                          onClick={() =>
                            setScreenshotIndex(
                              (Math.min(screenshotIndex, screenshots.length - 1) -
                                1 +
                                screenshots.length) %
                                screenshots.length
                            )
                          }
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-muted-foreground hover:text-foreground"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        {screenshots.map((s, i) => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setScreenshotIndex(i)}
                            className={`h-7 w-7 rounded-full text-xs font-bold ${
                              Math.min(screenshotIndex, screenshots.length - 1) === i
                                ? "bg-primary text-primary-foreground"
                                : "bg-secondary text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                        <button
                          type="button"
                          title="Nächstes Bild"
                          onClick={() =>
                            setScreenshotIndex(
                              (Math.min(screenshotIndex, screenshots.length - 1) + 1) %
                                screenshots.length
                            )
                          }
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-muted-foreground hover:text-foreground"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setFensterTab("dialog")}
                    className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover"
                  >
                    Weiter zum Erfassungs-Assistenten
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ) : fensterTab === "dialog" ? (
              <div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
                {chatBlock}
                {kiButtonBlock}
              </div>
            ) : (
              <div className="grid min-h-0 flex-1 grid-cols-2 gap-x-6 overflow-y-auto p-5">
                <div className="flex flex-col gap-3">
                  {typBlock}
                  {felderBlock}
                  {footerBlock}
                </div>
                <div className="flex flex-col gap-3">
                  {vorschauBlock || (
                    <p className="rounded-[10px] border border-dashed px-3.5 py-3 text-xs text-muted-foreground">
                      Noch keine KI-Zusammenfassung — im Tab „Erfassungs-Assistent“
                      erstellen oder die Felder links manuell ausfüllen.
                    </p>
                  )}
                  {screenshotsBlock}
                </div>
              </div>
            )}
          </div>
        ) : (
          (() => {
            // Akkordeon-Abschnitt: Kopf klappt auf und schließt die anderen;
            // erneuter Klick schließt. Nummern führen von oben nach unten.
            const sektion = (id: SektionId, titel: string, inhalt: React.ReactNode) => (
              <section className="shrink-0 rounded-[10px] border">
                <button
                  type="button"
                  onClick={() => setOffeneSektion((o) => (o === id ? null : id))}
                  className="flex w-full items-center justify-between px-3.5 py-2.5 text-[13px] font-bold"
                >
                  {titel}
                  <ChevronRight
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      offeneSektion === id ? "rotate-90" : ""
                    }`}
                  />
                </button>
                {offeneSektion === id && (
                  <div className="flex flex-col gap-3.5 border-t p-3.5">{inhalt}</div>
                )}
              </section>
            )
            return (
              <aside
                style={{ width: leisteBreite }}
                className="relative flex shrink-0 flex-col gap-3.5 overflow-y-auto rounded-xl border bg-card p-5 pl-6"
              >
                {/* Ziehgriff am linken Rand (Breite 380–900 px, gemerkt) */}
                <div
                  onPointerDown={leisteZiehen}
                  title="Breite ändern"
                  className="absolute inset-y-0 left-0 z-10 w-2 cursor-col-resize rounded-l-xl hover:bg-primary/20"
                />
                {/* Während des Ziehens schluckt das iframe sonst die Mausereignisse */}
                {leisteZieht && <div className="fixed inset-0 z-50 cursor-col-resize" />}
                <div className="flex items-center justify-between">
                  <h2 className="text-[15px] font-bold">Neue Meldung</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setFensterPos({
                        x: Math.max(16, Math.round(window.innerWidth / 2 - 700)),
                        y: Math.max(16, Math.round(window.innerHeight * 0.06)),
                      })
                      setFensterTab(kiVorschau ? "ergebnis" : "screenshot")
                      setAlsFenster(true)
                    }}
                    title="Als bewegliches Fenster öffnen"
                    className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    <Maximize2 className="h-3.5 w-3.5" /> Fenster
                  </button>
                </div>
                {sektion(
                  "dialog",
                  "1 · Dialog & KI",
                  <>
                    {chatBlock}
                    {kiButtonBlock}
                    {vorschauBlock}
                  </>
                )}
                {sektion("einordnung", "2 · Einordnung", typBlock)}
                {sektion("beschreibung", "3 · Beschreibung", felderBlock)}
                {sektion(
                  "screenshots",
                  `4 · Screenshots (${screenshots.length})`,
                  screenshotsBlock
                )}
                {footerBlock}
              </aside>
            )
          })()
        )
      })()}

      {/* ninox-Quelle: frisch aufgenommenes Bild → Annotations-Editor */}
      {rohBild && entwurfId !== null && (
        <AnnotationEditor
          bild={rohBild}
          onAbbrechen={() => setRohBild(null)}
          onFertig={(e) => annotationFertig.mutate(e)}
        />
      )}

      {/* Nachbearbeitung eines gespeicherten Screenshots (Fenster-Tab 1) */}
      {bearbeitung && entwurfId !== null && (
        <AnnotationEditor
          bild={bearbeitung.blob}
          onAbbrechen={() => setBearbeitung(null)}
          onFertig={(e) => bearbeitungFertig.mutate({ ...e, altId: bearbeitung.altId })}
        />
      )}
    </div>
  )
}
